import { type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import {
  useDeleteWorkspaceArtifact,
  type WorkspaceArtifact,
} from '@/lib/api/workspace-artifacts.js';

/** Confirm the irreversible removal of one artifact and all of its revisions. */
export function ArtifactDeleteDialog({
  workspaceId,
  artifact,
  onDeleted,
  onClose,
}: {
  workspaceId: string;
  artifact: WorkspaceArtifact;
  onDeleted?: () => void;
  onClose: () => void;
}): JSX.Element {
  const deletion = useDeleteWorkspaceArtifact(workspaceId);

  return (
    <AppDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Delete file?"
      description={`“${artifact.name}” and all of its versions will be permanently deleted from this workspace. This can't be undone.`}
    >
      <p className="mt-2 text-sm text-muted-foreground">
        Chat messages that reference it will remain, but the attachment will no
        longer open and will appear as “Not available to you”.
      </p>
      {deletion.isError && (
        <p role="alert" className="mt-2 text-sm text-destructive">
          {deletion.error.message}
        </p>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={deletion.isPending}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          disabled={deletion.isPending}
          onClick={() =>
            deletion.mutate(artifact.id, {
              onSuccess: () => {
                onDeleted?.();
                onClose();
              },
            })
          }
        >
          {deletion.isPending ? 'Deleting…' : 'Delete file'}
        </Button>
      </div>
    </AppDialog>
  );
}
